import type { DocumentAuthentication } from "../types/invoice";

/**
 * IRP Invoice Reference Number: a 64-hex SHA-256 digest. Keep in lockstep with
 * `irnPattern` in VIP's models/vendor_invoice_portal_models/invoice_extraction_models.go.
 */
const IRN_PATTERN = /^[0-9a-f]{64}$/i;

/** A seal or a signature was reported (VIP ADR 0038). */
export function hasSealOrSignature(d: DocumentAuthentication | null | undefined): boolean {
  return d?.seal_present === true || d?.signature_present === true;
}

/**
 * An IRP-registered GST e-invoice: IRN and e-invoice QR both reported, with a
 * well-formed IRN. The format check mirrors the server-side guard against
 * hallucinated booleans (VIP ADR 0045).
 */
export function hasEInvoiceMark(d: DocumentAuthentication | null | undefined): boolean {
  return (
    d?.irn_present === true &&
    d?.qr_code_present === true &&
    IRN_PATTERN.test((d?.irn_number ?? "").trim())
  );
}

/**
 * Whether the parser reported on the e-invoice keys at all. Absent keys are a
 * pre-v15 parse (render `—`); an explicit `false` is a verdict (render a cross).
 */
export function hasEInvoiceVerdict(d: DocumentAuthentication | null | undefined): boolean {
  return typeof d?.irn_present === "boolean" || typeof d?.qr_code_present === "boolean";
}

/**
 * Both legacy presence flags arrived as booleans. Mirrors the backend's
 * Judgeable(): a partial block or a pre-v12 parse carries no verdict and must
 * not be read as "no marks".
 */
export function isJudgeable(d: DocumentAuthentication | null | undefined): boolean {
  return typeof d?.seal_present === "boolean" && typeof d?.signature_present === "boolean";
}

/**
 * Single definition of the authentication policy, mirroring the backend's
 * Authenticated(): a seal, a signature, or a complete e-invoice mark.
 */
export function isAuthenticated(d: DocumentAuthentication | null | undefined): boolean {
  return hasSealOrSignature(d) || hasEInvoiceMark(d);
}
